// src/components/molecules/ChallengeCard.js

import React from "react";
import {
  Avatar,
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Button,
  useMediaQuery,
} from "@mui/material";
import ForwardRoundedIcon from "@mui/icons-material/ForwardRounded";
import { useTheme } from "styled-components";
import uploadCoverImage from "../../assets/upload-a-cover.webp";
import { useNavigate } from "react-router-dom";

const ChallengeCard = ({ challenge }) => {
  const { prize, hero, id: challengeId, title, creatorInfo } = challenge;
  const { profileImage, userName, creatorUid } = creatorInfo || {};
  const theme = useTheme();
  const navigate = useNavigate();

  // Custom media query for Galaxy Z Fold 5 (width between 340px and 350px)
  const isMobileFolded = useMediaQuery(
    "(min-width: 340px) and (max-width: 350px)"
  );

  // Array of possible background colors from theme
  const backgroundColors = [
    theme.colors.green,
    theme.colors.amber,
    theme.colors.red,
  ];

  // Select a random color from the array
  const randomBackgroundColor =
    backgroundColors[Math.floor(Math.random() * backgroundColors.length)];

  const handleViewChallenge = () => {
    if (!challengeId) {
      return;
    }
    navigate(`/challenge/${challengeId}`);
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        {/* Avatar */}
        <Avatar
          src={profileImage}
          alt="Profile Image"
          sx={{
            width: {
              xs: 60, // size for mobile devices
              sm: 60, // size for small tablets
              md: 60, // size for tablets
              lg: 60, // size for desktop
              xl: 60, // size for larger screens
            },
            height: {
              xs: 60, // size for mobile devices
              sm: 60, // size for small tablets
              md: 60, // size for tablets
              lg: 60, // size for desktop
              xl: 60, // size for larger screens
            },
            marginRight: 2, // Space between avatar and text
          }}
        />
        {/* Title and Username */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontFamily: "basic-sans",
              fontWeight: 700,
              color: theme.colors.white,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: isMobileFolded
                ? "1rem"
                : {
                    xs: "1.3em",
                    sm: "1em",
                    md: "1.1em",
                    lg: "1.3em",
                    xl: "1.5rem",
                  },
              maxWidth: isMobileFolded
                ? "170px"
                : {
                    xs: "235px",
                    sm: "225px",
                    md: "16vw",
                    lg: "230px",
                    xl: "320px",
                  },
            }}
          >
            {title || "Untitled Challenge"}
          </Typography>
          <Typography
            variant="body1"
            component="p"
            sx={{
              fontFamily: "basic-sans",
              fontSize: "1.2em",
              fontWeight: 500,
              color: randomBackgroundColor, // Use the random button color for userName
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={() => navigate(`/challenge/profile/${creatorUid}`)}
          >
            {userName || "Unknown Creator"}
          </Typography>
        </Box>
      </Box>

      {/* Challenge Card */}
      <Card
        sx={{
          maxWidth: 465,
          borderRadius: 5,
          position: "relative",
          mb: 4,
          backgroundColor: "transparent",
        }}
      >
        <CardMedia
          component="img"
          height="530"
          image={hero || uploadCoverImage} // Placeholder image if none is provided
          alt="Challenge Cover"
          sx={{ borderTopLeftRadius: 5, borderTopRightRadius: 5 }}
        />
        <Box
          sx={{
            position: "absolute",
            left: 0,
            bottom: 85,
            width: "100%",
            height: {
              xs: 165, // size for mobile devices
              sm: 135, // size for small tablets
              md: 95, // size for tablets
              lg: 125, // size for desktop
              xl: 165, // size for larger screens
            },
            backgroundColor: "rgba(18, 24, 69, .75)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            textAlign: "center", // Ensure Box content is center-aligned
          }}
        >
          <Typography
            variant="h5"
            component="h3"
            sx={{
              fontFamily: "basic-sans",
              fontWeight: "700",
              fontSize: {
                xs: "1.8rem",
                sm: "1.25rem",
                md: "1rem",
                lg: "1.4rem",
                xl: "2.0rem",
              }, // Responsive font size
              textAlign: "center", // Center the text within Typography
            }}
          >
            Winner's Prize
          </Typography>
          <Typography
            variant="h2"
            component="p"
            sx={{
              fontFamily: "basic-sans",
              fontWeight: "normal",
              fontSize: {
                xs: "1.2rem",
                sm: "1.5rem",
                md: "1.0rem",
                lg: "1.3rem",
                xl: "1.3rem",
              }, // Responsive font size for the prize amount
              textAlign: "center", // Center the text within Typography
            }}
          >
            {prize}
          </Typography>
        </Box>

        {/* "Take the Challenge" Button */}
        <CardContent
          sx={{
            backgroundColor: randomBackgroundColor,
            color: "white",
            textAlign: "center",
            height: 85,
            borderBottomLeftRadius: 5,
            borderBottomRightRadius: 5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            cursor: "pointer",
            transition: "transform 0.3s ease, background-color 0.3s ease",
            "&:hover": {
              backgroundColor: theme.colors.darkBlue, // Optional hover effect
            },
          }}
        >
          <Button
            variant="text"
            onClick={handleViewChallenge}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontFamily: "basic-sans",
              fontWeight: "600",
              lineHeight: {
                xs: "1.5rem",
                sm: "1.5rem",
                md: "1.5rem",
                lg: "1.75rem",
              },
              fontSize: {
                xs: "1.5rem",
                sm: "1.2rem",
                md: "1.15rem",
                lg: "1.75rem",
              }, // Responsive font size
              textTransform: "none",
            }}
          >
            <Typography
              variant="h6"
              component="h3"
              sx={{
                mr: 1,
                fontSize: {
                  md: "1.05rem",
                  xl: "1.7rem",
                },
              }}
            >
              Take the Challenge
            </Typography>
            <ForwardRoundedIcon
              sx={{
                fontSize: {
                  xs: "2rem",
                  sm: "2.4rem",
                  md: "2.8rem",
                  lg: "3.2rem",
                }, // Responsive size for the icon
              }}
            />
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ChallengeCard;
